import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Salaries from './pages/Salaries';
import SalaryDetail from './pages/SalaryDetail';
import Appraisals from './pages/Appraisals';
import DocumentsRedirect from './pages/DocumentsRedirect';
import AddEmployee from './pages/AddEmployee';
import AddSalary from './pages/AddSalary';
import EditEmployee from './pages/EditEmployee';
import EditSalary from './pages/EditSalary';
import EditAppraisal from './pages/EditAppraisal';
import AddAppraisal from './pages/AddAppraisal';
import AppraisalDetail from './pages/AppraisalDetail';
import Layout from './components/Layout';
import AuthUnavailable from './components/AuthUnavailable';
import Profile from './pages/Profile';
import Reports from './pages/Reports';
import ReportDetail from './pages/ReportDetail';
import NewSalarySheetReport from './pages/NewSalarySheetReport';
import ReportsLayout from './pages/ReportsLayout';
import NewReportTypeSelect from './pages/NewReportTypeSelect';
import UserManagement from './pages/UserManagement';
import NewMeezanBankTransferSheet from './pages/NewMeezanBankTransferSheet';
import TransactionFees from './pages/TransactionFees';
import Departments from './pages/Departments';
import Currencies from './pages/Currencies';
import PaymentMethods from './pages/PaymentMethods';
import NotFound from './pages/NotFound';
import OnboardingForm from './pages/OnboardingForm';
import OnboardingRequests from './pages/OnboardingRequests';
import OnboardingReview from './pages/OnboardingReview';
import ForceChangePassword from './pages/ForceChangePassword';
import Leaves from './pages/Leaves';
import Attendance from './pages/Attendance';
import AttendanceIgnored from './pages/AttendanceIgnored';
import MyAttendance from './pages/MyAttendance';
import AddLeave from './pages/AddLeave';
import LeaveDetail from './pages/LeaveDetail';
import LeaveBalances from './pages/LeaveBalances';
import LeaveTypes from './pages/LeaveTypes';
import MobileDevices from './pages/MobileDevices';
import OrgChart from './pages/OrgChart';
import OrgChartExport from './pages/OrgChartExport';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, authUnavailable } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // The session check got no answer (rate limited, a 5xx, the network), which says
  // nothing about the session, so this is not a reason to send anyone to /login.
  if (authUnavailable) return <AuthUnavailable />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Users flagged must_change_password are locked to /change-password until they pick a new one.
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Onboarding gate: a purely own-scoped user with no linked employee record can't use any
  // data page — every fetch 403s with ONBOARDING_REQUIRED and the axios interceptor bounces
  // them post-render (a visible flash). Redirect such users straight to the onboarding form
  // BEFORE any page mounts. Infra users (super_admin / HR) hold a read_all or read_department
  // permission somewhere, so they are NOT gated and keep full access without an employee record.
  // Read permissions off the `user` object (not the separate userPermissions state) so the
  // permission list is always consistent with the employee_id we check against — it can't
  // drift even if a future change breaks the paired setUser/setUserPermissions updates.
  const requiresOnboarding =
    user &&
    user.employee_id == null &&
    !(user.permissions || []).some(p => p.action === 'read_all' || p.action === 'read_department');
  if (requiresOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Guard for the /change-password page itself — only reachable when the flag is set.
const ForceChangePasswordRoute = () => {
  const { isAuthenticated, loading, user, authUnavailable } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (authUnavailable) return <AuthUnavailable />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.must_change_password) return <Navigate to="/" replace />;

  return <ForceChangePassword />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/change-password" element={<ForceChangePasswordRoute />} />
      {/* Outside Layout on purpose: the printable export has no sidebar/topbar
          to hide from the printed page, they are simply never mounted. Still
          behind ProtectedRoute - the export needs a session like any other
          page, it just isn't part of the app shell. */}
      <Route path="/org-chart/export" element={<ProtectedRoute><OrgChartExport /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="employees" element={<Employees />} />
        <Route path="employees/:id" element={<EmployeeDetail />} />
        <Route path="employees/add" element={<AddEmployee />} />
        <Route path="employees/:id/edit" element={<EditEmployee />} />
        <Route path="org-chart" element={<OrgChart />} />
        <Route path="salaries" element={<Salaries />} />
        <Route path="salaries/add" element={<AddSalary />} />
        <Route path="salaries/:id" element={<SalaryDetail />} />
        <Route path="salaries/:id/edit" element={<EditSalary />} />
        <Route path="appraisals" element={<Appraisals />} />
        <Route path="appraisals/add" element={<AddAppraisal />} />
        <Route path="appraisals/:id" element={<AppraisalDetail />} />
        <Route path="appraisals/:id/edit" element={<EditAppraisal />} />
        {/* Documents live in each employee's profile; old links land there. */}
        <Route path="documents/*" element={<DocumentsRedirect />} />
        <Route path="profile" element={<Profile />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="transaction-fees" element={<TransactionFees />} />
        <Route path="departments" element={<Departments />} />
        <Route path="currencies" element={<Currencies />} />
        <Route path="payment-methods" element={<PaymentMethods />} />

        {/* Onboarding Routes */}
        <Route path="onboarding" element={<OnboardingForm />} />
        <Route path="onboarding/requests" element={<OnboardingRequests />} />
        <Route path="onboarding/requests/:id" element={<OnboardingReview />} />

        {/* Leaves Routes */}
        <Route path="leaves" element={<Leaves />} />

        {/* Attendance Routes */}
        <Route path="attendance" element={<Attendance />} />
        <Route path="attendance/ignored" element={<AttendanceIgnored />} />
        <Route path="my-attendance" element={<MyAttendance />} />
        <Route path="mobile-devices" element={<MobileDevices />} />
        <Route path="timesheet-approvals" element={<Navigate to="/attendance?tab=approvals" replace />} />
        <Route path="leaves/new" element={<AddLeave />} />
        <Route path="leaves/balances" element={<LeaveBalances />} />
        <Route path="leaves/types" element={<LeaveTypes />} />
        <Route path="leaves/:id/edit" element={<AddLeave />} />
        <Route path="leaves/:id" element={<LeaveDetail />} />
        
        {/* Reports Routes */}
        <Route path="reports" element={<ReportsLayout />}>
          <Route index element={<Reports />} />
          <Route path="new" element={<NewReportTypeSelect />} />
          <Route path="new/salary-sheet" element={<NewSalarySheetReport />} />
          <Route path="new/salary_sheet" element={<NewSalarySheetReport />} />
          <Route path="new/meezan-bank-transfer" element={<NewMeezanBankTransferSheet />} />
          <Route path="new/meezan_bank_transfer_sheet" element={<NewMeezanBankTransferSheet />} />
          <Route path="history" element={<Reports />} />
          <Route path=":id" element={<ReportDetail />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </Router>
  );
};

export default App; 